/**
 * Centralni registry: endpoint URL → resolver funkcije za X-Role header.
 *
 * Redosled prioriteta u ApiService:
 *   1. req.headers['X-Role'] — eksplicitni caller override (dinamički slučajevi: consensusActor)
 *   2. ENDPOINT_ROLE_POLICY — statički poznati zahtevi za određeni endpoint
 *   3. resolveApiXRoleHeader — globalni fallback (priority lista iz JWT-a)
 *
 * Za novi endpoint dovoljno je dodati jedan red u ENDPOINT_ROLE_POLICY.
 */

import {
  resolveObligationSaveXRole,
  resolveAccountMembershipXRole,
  resolveRequestedXRole,
  isPlatformAdmin,
} from '@/shared/auth/obligationAuth';
import { API_ENDPOINTS } from './endpoints';

const OBL = API_ENDPOINTS.OGP.OBLIGATION;
const ACC = API_ENDPOINTS.ACCOUNT_ORGANIZATION;
const DICT = API_ENDPOINTS.DICTIONARY;
const NOTICE = API_ENDPOINTS.NOTICE;

/**
 * Mapa endpoint URL string → resolver funkcija `(user) => string | null`.
 * Resolver vraća string uloge za X-Role header; `null` znači da nema posebnog zahteva — koristi se globalni fallback.
 * @type {Map<string, (user: object|null|undefined) => (string|null)>}
 */
export const ENDPOINT_ROLE_POLICY = new Map([
  [OBL.SAVE_OR_UPDATE, resolveObligationSaveXRole],
  [OBL.DRAFT_SAVE_OR_UPDATE, resolveObligationSaveXRole],
  [OBL.DRAFT_SEARCH, resolveObligationSaveXRole],
  [OBL.DRAFT_SUBMIT, resolveObligationSaveXRole],
  [OBL.PROPOSAL_SAVE_OR_UPDATE, resolveObligationSaveXRole],
  [ACC.REQUEST_ACCESS, resolveAccountMembershipXRole],
  [DICT.SAVE_OR_UPDATE, () => 'ADMIN'],
  [NOTICE.SAVE_OR_UPDATE, (user) => (isPlatformAdmin(user) ? 'ADMIN' : null)],
  [NOTICE.INVALIDATE, (user) => (isPlatformAdmin(user) ? 'ADMIN' : null)],
  [OBL.MODERATOR_ACCEPT, (user) => resolveRequestedXRole(user, 'MODERATOR', { allowFunctionalModerator: true })],
  [OBL.MODERATOR_REJECT, (user) => resolveRequestedXRole(user, 'MODERATOR', { allowFunctionalModerator: true })],
]);

/**
 * Vraća X-Role string za dati endpoint, ili `null` ako endpoint nije u registry-ju.
 * `null` signalizira da se koristi globalni fallback (`resolveApiXRoleHeader`).
 *
 * @param {string} url - URL endpoint-a koji se poziva.
 * @param {object|null|undefined} user - Korisnik sa `loginData` (JWT uloge i organizacije).
 * @returns {string|null} Rezolvovana uloga za X-Role header ili `null` za globalni fallback.
 */
export function resolveRoleForEndpoint(url, user) {
  if (!url) return null;
  const resolver = ENDPOINT_ROLE_POLICY.get(url);
  return resolver ? resolver(user) : null;
}
